import React, {
  ReactElement,
  forwardRef,
  useRef,
  useState,
  MouseEvent,
  TouchEvent,
  useCallback,
  CSSProperties,
  useMemo,
  useEffect,
} from "react";
import classNames from "classnames";
import uniqueId from "lodash.uniqueid";
import {
  CarouselItem,
  CarouselProps,
  CarouselVariant,
  MouseOrTouchEvent,
} from "./carousel.types";
import useResizeObserver from "../utils/useResizeObserver";
import { CarouselCssOverrides } from "./css-overrides";

export const Carousel = forwardRef<HTMLDivElement, CarouselProps>(
  (props, ref): ReactElement => {
    const {
      variant: variantProp,
      type,
      items,
      carouselTitle,
      className,
      itemWidth,
      itemHeight,
      dots = true,
      flexWidthBreakpoints,
      itemMargin,
      itemPadding,
      autoScrollCentering,
      preventCenteringOnClick,
      autoPlayInterval,
      skipCarouselTargetId,
      skipCarouselLabel = "Skip Carousel",
      previousImageAriaLabel = "Previous Image",
      nextImageAriaLabel = "Next Image",
      pauseCarouselAriaLabel = "Pause Carousel",
      onItemClick,
      onClick,
      children,
      ...rest
    } = props;
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [totalWidth, setTotalWidth] = useState<number>(0);
    const [itemsWidth, setItemsWidth] = useState<number[]>([]);
    const [shift, setShift] = useState<number>(0);
    const [preventShift, setPreventShift] = useState<boolean>(false);
    const [pause, setPause] = useState<boolean>(false);
    const [flexItemWidth, setFlexItemWidth] = useState<number | undefined>(
      undefined
    );
    const { current: _uniqueClassSuffix } = useRef(uniqueId("_"));

    const carouselContainerRef = useRef<HTMLDivElement>(null);
    const carouselItemsRef = useRef<HTMLDivElement[]>([]);
    const skipCarouselRef = useRef<HTMLButtonElement>(null);

    const mouseDown = useRef<boolean>(false);
    const mouseDownX = useRef<number>(0);
    const mouseDownTimeStamp = useRef<number>(0);
    const mouseDownCurrentIndex = useRef<number | undefined>(undefined);
    const temporaryShift = useRef<number | undefined>(undefined);
    const isMouseMove = useRef<boolean>(false);
    const minShift = useRef<number>(0);
    // const isInitialRender = useRef<boolean>(true);

    // Merging new and deprecated property
    const variant = variantProp || type || CarouselVariant.MULTIPLE_PANEL;

    const isHero = variant === CarouselVariant.HERO;
    const isFilmstrip = variant === CarouselVariant.FILMSTRIP;
    const isMultiplePanel = variant === CarouselVariant.MULTIPLE_PANEL;

    const itemsCount = children
      ? React.Children.count(children)
      : items?.length || 1;

    const avarageItemWidth = useMemo(() => {
      return totalWidth / itemsCount;
    }, [itemsCount, totalWidth]);

    const calculateDistanceToItemCenter = useCallback(
      (clickedIndex: number): number => {
        let startToItemCenter: number = 0;
        itemsWidth.forEach((itemWidth, index) => {
          if (index < clickedIndex) {
            startToItemCenter += itemWidth;
          } else if (index === clickedIndex) {
            startToItemCenter += itemWidth / 2;
          }
        });

        return startToItemCenter;
      },
      [itemsWidth]
    );

    useResizeObserver({
      ref: carouselContainerRef as React.RefObject<Element>,
      onResize: ({ width: carouselWidth }) => {
        const _carouselWidth = carouselWidth || 1;

        let customFlexBreakpoint;
        let customItemsPerPage: number | undefined;
        let teaserWidth: number | undefined;
        // Flex item width, including its padding and margin
        let flexItemOuterWidth: number | undefined;
        const variantPadding = isMultiplePanel ? 24 : 0;
        const variantMargin = isMultiplePanel ? 18 : 6;

        if (!isHero && flexWidthBreakpoints && flexWidthBreakpoints.length) {
          customFlexBreakpoint = flexWidthBreakpoints.find((item) => {
            return _carouselWidth >= item.min && _carouselWidth <= item.max;
          });
          customItemsPerPage = customFlexBreakpoint?.itemsPerPage;

          if (customItemsPerPage) {
            if (customFlexBreakpoint?.hideTeaser) {
              teaserWidth = 0;
            } else {
              teaserWidth = customFlexBreakpoint?.teaserWidth ?? 50;
            }
            flexItemOuterWidth =
              (_carouselWidth - teaserWidth) / customItemsPerPage;
          }
        }

        const items = carouselItemsRef.current;
        const itemsWidth: number[] = [];
        let totalWidth: number = 0;

        items.forEach((item, i) => {
          const itemWidth = flexItemOuterWidth ?? item?.clientWidth ?? 0;
          totalWidth += itemWidth;
          itemsWidth[i] = itemWidth;
        });

        if (flexItemOuterWidth) {
          // Set the width without taking into account margins and paddings
          setFlexItemWidth(
            flexItemOuterWidth -
              ((itemPadding ?? variantPadding) * 2 +
                (itemMargin ?? variantMargin) * 2)
          );
        } else {
          // Clear state if custom flex breakpoint was not provided
          setFlexItemWidth(undefined);
        }

        let offset: number;

        // On initial render establish the first movable "currentIndex", so the right arrow can start the scroll animation on the first click
        // Update: Removed feature as setting first element as "current" is more expected by users
        // if (isInitialRender.current === true) {
        //   let initialItemsWidth = 0;
        //   const lastVisibleIndex = itemsWidth.findIndex((width, i) => {
        //     initialItemsWidth += width;
        //     return initialItemsWidth > _carouselWidth;
        //   });
        //   setCurrentIndex(Math.floor((lastVisibleIndex + 1) / 2) - 1);
        //   isInitialRender.current = false;
        // }

        // Use translateX transformation only when all items width is greater than container width
        if (_carouselWidth > totalWidth) {
          setPreventShift(true);
          offset = 0;
        } else {
          setPreventShift(false);

          const centerPointX = totalWidth / 2;
          const bleed = (totalWidth - _carouselWidth) / 2;

          minShift.current = bleed;

          offset = centerPointX - calculateDistanceToItemCenter(currentIndex);
          if (offset > bleed) {
            offset = bleed;
          }

          setTotalWidth(totalWidth);
          setItemsWidth(itemsWidth);
        }

        setShift(offset);
        carouselContainerRef?.current?.setAttribute(
          "style",
          `transform: translate3d(${offset}px, 0px, 0px);`
        );
      },
    });

    const centerSelectedItem = useCallback(
      (clickedIndex: number): void => {
        let _clickedIndex = clickedIndex;

        if (clickedIndex < 0) {
          _clickedIndex = 0;
        }
        if (clickedIndex > itemsCount - 1) {
          _clickedIndex = itemsCount - 1;
        }
        setCurrentIndex(_clickedIndex);

        if (!preventShift) {
          const centerPointX = totalWidth / 2;

          let offset =
            centerPointX - calculateDistanceToItemCenter(clickedIndex);
          // When items go beyond left border
          if (offset > minShift.current) {
            offset = minShift.current;
          }
          // When items go beyond right border
          if (offset < -minShift.current) {
            offset = -minShift.current;
          }
          setShift(offset);

          carouselContainerRef?.current?.setAttribute(
            "style",
            `transform: translate3d(${offset}px, 0px, 0px);transition: transform ease 0.4s`
          );
        }
      },
      [calculateDistanceToItemCenter, itemsCount, preventShift, totalWidth]
    );

    useEffect(() => {
      if (isHero && autoPlayInterval && !pause) {
        const intervalId = setInterval(() => {
          centerSelectedItem(
            currentIndex === itemsCount - 1 ? 0 : currentIndex + 1
          );
        }, autoPlayInterval * 1000);

        return () => clearInterval(intervalId);
      }
    }, [
      autoPlayInterval,
      centerSelectedItem,
      currentIndex,
      isHero,
      itemsCount,
      pause,
    ]);

    const clearAfterMouseMove = (
      e: MouseOrTouchEvent,
      source: "mouse" | "touch" | undefined,
      mouseLeave: boolean = false
    ): void => {
      if (isMouseMove.current) {
        // Update translateX value when: mouseMove ends || mouseLeave || onTouch ends
        setShift(temporaryShift.current || shift);

        if (autoScrollCentering || isHero) {
          const pageX =
            source === "mouse"
              ? (e as MouseEvent<HTMLDivElement>).pageX
              : (e as TouchEvent<HTMLDivElement>).changedTouches[0].pageX;

          const mouseShift = pageX - mouseDownX.current;
          const mouseVelocity = Math.abs(
            100 * (mouseShift / (e.timeStamp - mouseDownTimeStamp.current))
          );

          // Handling situations when a quick and short mouse or gesture movement is performed
          if (
            !mouseLeave &&
            mouseDownCurrentIndex.current === currentIndex &&
            mouseVelocity > 15
          ) {
            if (mouseShift > 0) {
              centerSelectedItem(currentIndex - 1);
            }
            if (mouseShift < 0) {
              centerSelectedItem(currentIndex + 1);
            }
            // Handling situations when longer or slower mouse or gesture movement is performed
          } else {
            centerSelectedItem(currentIndex);
          }
        }
      }

      mouseDown.current = false;
      isMouseMove.current = false;
    };

    const onMouseDownOrTouchStart = (
      e: MouseOrTouchEvent,
      source: "mouse" | "touch"
    ): void => {
      mouseDown.current = true;
      mouseDownX.current =
        source === "mouse"
          ? (e as MouseEvent<HTMLDivElement>).pageX
          : (e as TouchEvent<HTMLDivElement>).targetTouches[0].pageX;
      mouseDownTimeStamp.current = e.timeStamp;
      mouseDownCurrentIndex.current = currentIndex;
    };

    const onMouseOrTouchMove = (
      e: MouseOrTouchEvent,
      source: "mouse" | "touch"
    ): void => {
      let offset: number = 0;
      if (mouseDown.current === true && !preventShift) {
        isMouseMove.current = true;
        const pageX =
          source === "mouse"
            ? (e as MouseEvent<HTMLDivElement>).pageX
            : (e as TouchEvent<HTMLDivElement>).targetTouches[0].pageX;
        const mouseOffset = mouseDownX.current - pageX;

        offset = shift - mouseOffset;
        // When items go beyond left border
        if (offset > minShift.current) {
          offset = minShift.current;
        }
        // When items go beyond right border
        if (offset < -minShift.current) {
          offset = -minShift.current;
        }

        /**
         * Highlight item while mouse/touch movement
         * (item position detection relative to the center of the container)
         **/
        // Map translateX values to positive value axis, e.g. "10 -> 0 -> -10" to "0 -> 20"
        const mappedShiftValue = totalWidth / 2 - offset;
        // Set currentIndex, when item overlaps with center line (mappedTranslateValue)
        // Visual explanation:
        //   |       5       | --> current transform position (mapped translateX)
        // |0-2|2-4|4-6|6-8| --> translateX values divided into groups one element wide
        // |i0 |i1 |i2 |i3 | --> mapped indexes
        // => current index = 2, because 5/2 ~ 2
        setCurrentIndex(Math.floor(mappedShiftValue / avarageItemWidth));

        carouselContainerRef?.current?.setAttribute(
          "style",
          `transform: translate3d(${offset}px, 0px, 0px); transition: transform ease-out 0.2s;`
        );
      }
      temporaryShift.current = offset;
    };

    const determineSize = useCallback(
      (specificWidth?: number, specificHeight?: number): CSSProperties => {
        let defaultVariantSize: number;

        if (isHero) {
          defaultVariantSize = 540;
        } else if (isFilmstrip) {
          defaultVariantSize = 200;
        } else defaultVariantSize = 224;

        return {
          width: !isHero
            ? specificWidth || itemWidth || defaultVariantSize
            : undefined,
          height: specificHeight || itemHeight || defaultVariantSize,
        };
      },
      [isFilmstrip, isHero, itemHeight, itemWidth]
    );

    const renderDots = (): ReactElement => (
      <div className="spark-carousel__dots">
        {Array.from({ length: itemsCount }).map((_, index) => (
          <div
            key={index}
            tabIndex={-1}
            className={classNames({
              selected: index === currentIndex,
            })}
            onClick={(): void => centerSelectedItem(index)}
          ></div>
        ))}
      </div>
    );

    const carouselClasses = classNames(
      "spark-carousel ready ",
      {
        rightbound:
          // Topic for discussion - whether the leftmost position of the carousel should remove the left arrow, even though the dot indicator is not yet in the first position
          // minShift.current <= shift || currentIndex === 0,
          currentIndex === 0,
        leftbound:
          // Topic for discussion - whether the rightmost position of the carousel should remove the right arrow, even though the dot indicator is not yet in the last position
          // -minShift.current >= shift || currentIndex === itemsCount - 1,
          currentIndex === itemsCount - 1,
        pause,
      },
      className
    );

    // When Carousel uses children as its elements
    const renderCarouselChild = (item: ReactElement): ReactElement => (
      <div
        style={{
          width: !isHero ? flexItemWidth || itemWidth : undefined,
          height: itemHeight,
        }}
      >
        {item}
      </div>
    );

    // When Carousel uses `items` property as its elements
    const renderCarouselItem = (item: CarouselItem): ReactElement => (
      <div style={determineSize(flexItemWidth ?? item.width, item.height)}>
        <img
          src={item.img}
          alt={item.altText || ""}
          // Topic to discuss - on EDL's css Hero maxWidth is set to "none", which affect issues with Modal containing Carousel (it's shifted outside the view) and img not being centered on narrow screen
          style={
            !isHero
              ? {
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }
              : { objectFit: "cover", maxWidth: "100%" }
          }
        />
      </div>
    );

    return (
      <div
        aria-label={carouselTitle}
        data-spark-carousel-panel={isFilmstrip ? "true" : undefined}
        data-spark-carousel-hero={isHero ? "true" : undefined}
        {...rest}
        ref={ref}
        onKeyDown={(e): void => {
          if (e.key === "Escape" && skipCarouselTargetId && !isHero) {
            skipCarouselRef.current?.focus();
          }
        }}
        className={carouselClasses}
        role={isHero ? "region" : undefined}
      >
        {isFilmstrip ? (
          <div
            className={classNames({
              "spark-carousel__header": !!carouselTitle,
              "spark-mar-t-2": !carouselTitle,
            })}
          >
            {carouselTitle ? <h4>{carouselTitle}</h4> : null}
          </div>
        ) : null}
        <div className="spark-carousel__outer-container">
          {isHero ? (
            <div className="spark-carousel__controls">
              {autoPlayInterval ? (
                <button
                  type="button"
                  aria-label={pauseCarouselAriaLabel}
                  className="spark-carousel__pause"
                  onClick={(): void => setPause((val) => !val)}
                >
                  <i className="spark-icon-video-control-pause spark-icon--fill" />
                </button>
              ) : null}
              <button
                type="button"
                aria-label={previousImageAriaLabel}
                className="spark-carousel__back"
                onClick={(): void => centerSelectedItem(currentIndex - 1)}
              >
                <i className="spark-icon-arrow-chevron-left spark-icon--line" />
              </button>
              <button
                type="button"
                aria-label={nextImageAriaLabel}
                className="spark-carousel__forward"
                onClick={(): void => centerSelectedItem(currentIndex + 1)}
              >
                <i className="spark-icon-arrow-chevron-right spark-icon--line" />
              </button>
              {dots ? renderDots() : null}
            </div>
          ) : null}
          <div className="spark-carousel__container-mask">
            {skipCarouselTargetId && !isHero ? (
              <button
                className="spark-carousel__skip"
                ref={skipCarouselRef}
                onClick={(): void => {
                  const targetElement =
                    document?.getElementById(skipCarouselTargetId);
                  targetElement?.focus();
                }}
              >
                {skipCarouselLabel}
              </button>
            ) : null}
            <div
              className="spark-carousel__container"
              ref={carouselContainerRef}
              onMouseUp={(e): void => clearAfterMouseMove(e, "mouse")}
              onMouseLeave={(e): void => clearAfterMouseMove(e, "mouse", true)}
              onTouchEnd={(e): void => clearAfterMouseMove(e, "touch")}
              onMouseDown={(e): void => onMouseDownOrTouchStart(e, "mouse")}
              onTouchStart={(e): void => onMouseDownOrTouchStart(e, "touch")}
              onMouseMove={(e): void => onMouseOrTouchMove(e, "mouse")}
              onTouchMove={(e): void => onMouseOrTouchMove(e, "touch")}
            >
              {(children
                ? React.Children.toArray(children as any)
                : items
              )?.map((item, index) => {
                const _onClick = children
                  ? onItemClick
                  : (item as CarouselItem).onClick ?? onItemClick;
                const itemId = children
                  ? (item as ReactElement<{ id?: string }>).props?.id ??
                    index.toString()
                  : (item as CarouselItem).id ?? index.toString();

                return (
                  <div
                    className={classNames("spark-carousel__item", {
                      selected: index === currentIndex,
                    })}
                    key={index}
                    tabIndex={isHero && !_onClick ? -1 : 0}
                    ref={(node: HTMLDivElement): void => {
                      // Assign node to carouselItems collection
                      carouselItemsRef.current[index] = node;
                    }}
                    onFocus={(): void => centerSelectedItem(index)}
                    onMouseUp={(e): void => {
                      // Do not trigger onClick and centering when mouseMove event is occurring
                      // && do not trigger onClick when mouseDown was outside the carousel
                      if (!isMouseMove.current && mouseDown.current === true) {
                        _onClick?.(itemId);
                        if (
                          !preventCenteringOnClick &&
                          (e.target as HTMLElement)?.nodeName !== "A" &&
                          (e.target as HTMLElement)?.nodeName !== "BUTTON"
                        ) {
                          centerSelectedItem(index);
                        }
                      }
                    }}
                    onMouseDown={(e): void => e.preventDefault()}
                    onKeyDown={
                      _onClick
                        ? (e): void => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              _onClick?.(itemId);
                            }
                          }
                        : undefined
                    }
                  >
                    <div
                      className={classNames("spark-carousel__item--content", {
                        "spark-carousel__panel": isMultiplePanel,
                        [`spark-carousel__panel-custom-padding${_uniqueClassSuffix}`]:
                          itemPadding !== undefined,
                      })}
                      style={{
                        marginLeft: itemMargin,
                        marginRight: itemMargin,
                      }}
                    >
                      {children
                        ? renderCarouselChild(item as ReactElement)
                        : renderCarouselItem(item as CarouselItem)}
                    </div>
                    {itemPadding !== undefined ? (
                      <CarouselCssOverrides
                        itemPadding={itemPadding}
                        classSuffix={_uniqueClassSuffix}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
          {!isHero ? (
            <div className="spark-carousel__controls">
              <div
                className="spark-carousel__back"
                onClick={(): void => centerSelectedItem(currentIndex - 1)}
              >
                <i
                  aria-label="Move carousel back"
                  className="spark-icon-arrow-chevron-left spark-icon--fill"
                />
              </div>
              <div
                className="spark-carousel__forward"
                // Topic to discuss - Carousel doesn't move when currentIndex === 0 and we click right arrow.
                // Should skip (focus) to further item to induce Carousel movement?
                onClick={(): void => centerSelectedItem(currentIndex + 1)}
              >
                <i
                  aria-label="Move carousel forward"
                  className="spark-icon-arrow-chevron-right spark-icon--fill"
                />
              </div>
              {dots && isMultiplePanel ? renderDots() : null}
            </div>
          ) : null}
        </div>
      </div>
    );
  }
);

Carousel.displayName = "Carousel";
